import React from "react";
import { useParams } from "react-router-dom";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

const Wrap = ({ title, eyebrow, children }) => (
  <div className="max-w-4xl mx-auto px-4 py-16">
    {eyebrow && <span className="rle-eyebrow">{eyebrow}</span>}
    <h1 className="mt-3 font-display font-extrabold text-3xl sm:text-4xl text-slate-900">{title}</h1>
    <div className="mt-8 prose prose-slate max-w-none text-slate-700">{children}</div>
  </div>
);

export function About() {
  return (
    <Wrap eyebrow="About" title="Built for real life, not textbooks.">
      <p>English Ready is an educational language-learning and communication-practice platform for migrants, newcomers and international professionals living in — or moving to — Australia, Canada and the UK.</p>
      <p>We focus on practical, useful English for everyday life and work: introducing yourself at a new job, calling a doctor, asking about a rental, or explaining a project delay to your manager. Not textbook drills.</p>
      <p>This is an independent product and not affiliated with any government, immigration authority or examination body.</p>
    </Wrap>
  );
}

export function FAQ() {
  const items = [
    { q: "Is this really free to start?", a: "Yes. Sign up, complete onboarding and try a starter lesson and a real-life scenario at no cost." },
    { q: "Do you guarantee I'll become fluent?", a: "No. We provide structured practice and feedback. Progress depends on you." },
    { q: "Is this an official English exam?", a: "No. It is not IELTS, PTE, CLB or any government-recognised test." },
    { q: "Which country should I choose?", a: "Choose the country you live in or plan to move to. You can change your English variant later." },
    { q: "When will voice practice be available?", a: "The UI is designed for voice. Voice providers will be connected in a later release." },
    { q: "Can I cancel any time?", a: "Yes. Paid plans can be cancelled from your account settings." },
  ];
  return (
    <Wrap eyebrow="FAQ" title="Frequently asked questions">
      <Accordion type="single" collapsible className="w-full">
        {items.map((it, i) => (
          <AccordionItem key={i} value={`i-${i}`} data-testid={`faq-item-${i}`}>
            <AccordionTrigger className="text-left">{it.q}</AccordionTrigger>
            <AccordionContent>{it.a}</AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </Wrap>
  );
}

export function Contact() {
  return (
    <Wrap eyebrow="Contact" title="Get in touch">
      <p>Questions, feedback, partnership requests — we'd love to hear from you.</p>
      <p>Email: <a href="mailto:hello@englishready.online">hello@englishready.online</a></p>
      <p className="text-sm text-slate-500">We do not provide immigration, legal, medical or financial advice.</p>
    </Wrap>
  );
}

export function Legal() {
  const { kind } = useParams();
  const map = {
    privacy: { title: "Privacy Policy", body: `We collect the personal information you provide when creating an account (email, name) and information generated as you use the product (lessons completed, scenarios attempted, mistakes). We use this information to operate and improve the product. This policy is a placeholder pending legal review.` },
    terms: { title: "Terms of Service", body: `By using English Ready you agree to use the service lawfully. English Ready is an educational language-learning and communication-practice platform. It does not provide immigration, legal, medical or financial advice, and does not guarantee any language-proficiency, IELTS/PTE/CLB score, employment or immigration outcome. Placeholder pending legal review.` },
    refund: { title: "Refund Policy", body: `Paid subscriptions can be cancelled at any time from your account. We may consider refunds within 14 days of the initial paid purchase if the service has not been substantially used. Placeholder pending legal review.` },
    cookies: { title: "Cookie Policy", body: `We use strictly necessary cookies to keep you signed in. We do not use advertising cookies at this time. Placeholder pending legal review.` },
  };
  const c = map[kind] || map.privacy;
  return (
    <Wrap eyebrow="Legal" title={c.title}>
      <p>{c.body}</p>
    </Wrap>
  );
}
